import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  Trash2,
  Inbox,
  Search,
  ExternalLink,
} from "lucide-react";
import { COMPANY } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORAGE_TOKEN = "lici_admin_token";
const STORAGE_EMAIL = "lici_admin_email";

function clearAdminStorage() {
  try {
    localStorage.removeItem(STORAGE_TOKEN);
    localStorage.removeItem(STORAGE_EMAIL);
    sessionStorage.removeItem(STORAGE_TOKEN);
    sessionStorage.removeItem(STORAGE_EMAIL);
  } catch {
    /* ignore */
  }
}

function confirmAction(msg) {
  return globalThis.confirm ? globalThis.confirm(msg) : true;
}

function performLogout(navigate) {
  clearAdminStorage();
  navigate("/admin/login", { replace: true });
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [adminEmail] = useState(() => localStorage.getItem(STORAGE_EMAIL) || "");
  const [token] = useState(() => localStorage.getItem(STORAGE_TOKEN) || "");
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    document.title = "Dashboard Admin · Impresa Edile Lici";
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/contact-requests`, { headers });
      setItems(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sessione scaduta, rifai il login.");
        logout();
        return;
      }
      toast.error("Errore nel caricamento delle richieste.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => performLogout(navigate);

  const removeItem = async (id) => {
    const ok = confirmAction("Eliminare definitivamente questa richiesta?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/contact-requests/${id}`, { headers });
      setItems((cur) => cur.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Richiesta eliminata.");
    } catch {
      toast.error("Errore nell'eliminazione.");
    }
  };

  const fmtDate = (iso) =>
    new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));

  const filtered = items.filter((it) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      it.nome.toLowerCase().includes(q) ||
      it.email.toLowerCase().includes(q) ||
      it.telefono.toLowerCase().includes(q) ||
      it.servizio.toLowerCase().includes(q) ||
      it.messaggio.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#FAFAFA] flex flex-col">
      {/* Topbar */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="Logo" className="h-10 w-10 object-contain" />
            <div>
              <div className="font-serif text-base text-[#0F5132] leading-tight">Dashboard</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                Richieste Preventivo
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[#4B5563]">{adminEmail}</span>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#0F5132] hover:underline"
              data-testid="admin-view-site"
            >
              <ExternalLink className="h-4 w-4" /> Sito
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#0F5132] text-white text-sm hover:bg-[#0A3622]"
            >
              <LogOut className="h-4 w-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-8 lg:px-12 py-8">
        {/* Stats + actions */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl text-[#0F5132]">Richieste ricevute</h1>
            <p className="text-sm text-[#4B5563] mt-1">
              {items.length} richieste totali · {filtered.length} visualizzate
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca per nome, email, servizio…"
                data-testid="admin-search"
                className="pl-9 pr-4 py-2.5 rounded-full border border-[#E5E7EB] bg-white w-64 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5132]"
              />
            </div>
            <button
              onClick={fetchItems}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E5E7EB] text-[#0F5132] text-sm hover:bg-black/5"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Aggiorna
            </button>
          </div>
        </div>

        {/* List + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7" data-testid="admin-requests-list">
            {loading ? (
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 text-center text-[#4B5563]">
                Caricamento…
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white border border-[#E5E7EB] rounded-2xl p-12 text-center">
                <Inbox className="h-12 w-12 text-[#0F5132]/40 mx-auto mb-4" />
                <p className="text-[#4B5563]">
                  {items.length === 0
                    ? "Ancora nessuna richiesta. Le richieste compariranno qui appena un cliente compila il form sul sito."
                    : "Nessun risultato per la tua ricerca."}
                </p>
              </div>
            ) : (
              <ul className="space-y-3">
                {filtered.map((it) => (
                  <li
                    key={it.id}
                    onClick={() => setSelected(it)}
                    data-testid={`admin-request-${it.id}`}
                    className={`cursor-pointer p-5 rounded-2xl bg-white border transition-all ${
                      selected?.id === it.id
                        ? "border-[#0F5132] shadow-md"
                        : "border-[#E5E7EB] hover:border-[#0F5132]/40 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-serif text-lg text-[#0F5132]">{it.nome}</span>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#D97706] bg-[#D97706]/10 px-2 py-0.5 rounded-full">
                            {it.servizio}
                          </span>
                        </div>
                        <p className="text-sm text-[#4B5563] line-clamp-2">{it.messaggio}</p>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#4B5563]">
                          <span className="flex items-center gap-1">
                            <Phone className="h-3.5 w-3.5" /> {it.telefono}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> {it.email}
                          </span>
                          <span>{fmtDate(it.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Detail */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-24 bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-7">
              {!selected ? (
                <div className="text-center py-12">
                  <Inbox className="h-10 w-10 text-[#0F5132]/30 mx-auto mb-3" />
                  <p className="text-sm text-[#4B5563]">
                    Seleziona una richiesta dalla lista per vederne i dettagli.
                  </p>
                </div>
              ) : (
                <div data-testid="admin-detail">
                  <div className="flex items-start justify-between gap-3 mb-5">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-[#D97706] font-medium">
                        {selected.servizio}
                      </div>
                      <h3 className="font-serif text-2xl text-[#0F5132] mt-1">{selected.nome}</h3>
                      <div className="text-xs text-[#4B5563] mt-1">{fmtDate(selected.created_at)}</div>
                    </div>
                    <button
                      onClick={() => removeItem(selected.id)}
                      data-testid="admin-delete"
                      className="p-2 rounded-full border border-[#E5E7EB] text-[#4B5563] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition"
                      aria-label="Elimina"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-4 mt-4">
                    <a
                      href={`tel:${selected.telefono}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E7EB] hover:border-[#0F5132] transition"
                    >
                      <Phone className="h-4 w-4 text-[#0F5132]" />
                      <span className="text-sm font-medium text-[#111827]">{selected.telefono}</span>
                    </a>
                    <a
                      href={`mailto:${selected.email}`}
                      className="flex items-center gap-3 p-4 rounded-xl bg-[#FAFAFA] border border-[#E5E7EB] hover:border-[#0F5132] transition break-all"
                    >
                      <Mail className="h-4 w-4 text-[#0F5132] flex-shrink-0" />
                      <span className="text-sm font-medium text-[#111827]">{selected.email}</span>
                    </a>
                  </div>

                  <div className="mt-6">
                    <div className="text-xs uppercase tracking-widest text-[#4B5563] mb-2">
                      Messaggio
                    </div>
                    <p className="text-sm text-[#111827] leading-relaxed whitespace-pre-line">
                      {selected.messaggio}
                    </p>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row gap-2">
                    <a
                      href={`https://wa.me/${selected.telefono.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                        `Buongiorno ${selected.nome.split(" ")[0]}, sono Lici. Ho ricevuto la sua richiesta di preventivo.`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90"
                    >
                      Rispondi su WhatsApp
                    </a>
                    <a
                      href={`mailto:${selected.email}?subject=${encodeURIComponent(
                        "Richiesta preventivo Impresa Edile Lici"
                      )}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0F5132] text-white text-sm font-medium hover:bg-[#0A3622]"
                    >
                      Rispondi via Email
                    </a>
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
